/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-runloop, ember/require-tagless-components */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { alias } from '@ember/object/computed';
import { computed } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import { isEmpty } from '@ember/utils';

// D3 imports
import { select } from 'd3-selection';
import { scaleLinear, scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { max } from 'd3-array';
import { axisBottom, axisLeft } from 'd3-axis';
import { area as d3Area, line as d3Line, stack as d3Stack } from 'd3-shape';
import tip from '@linc-technologies/linced-common/utils/d3-tip';
import {
  attributeBindings,
  classNames,
  tagName,
} from '@ember-decorators/component';

@tagName('svg')
@classNames('chart', 'chart-stacked-area')
@attributeBindings('width', 'height', 'viewBox', 'preserveAspectRatio')
export default class ChartStackedArea extends Component {
  preserveAspectRatio = 'xMinYMin meet';

  // Dynamic margin based on Y-axis label visibility
  get margin() {
    return {
      top: 20,
      right: 20,
      bottom: 30,
      left: this.showYAxisLabels ? 40 : 20,
    };
  }

  colors = schemeCategory10;
  @tracked showYAxisLabels = false;
  hideTooltip = false;
  lineColor = '#000000';

  @alias('lineColor')
  dotColor;

  showLabels = true;
  levels = null;
  data = [];

  @computed('data.[]', 'levels.[]', 'lineData.[]')
  get processedData() {
    let rawData = this.data;
    if (isEmpty(rawData)) {
      return rawData;
    }

    let extrapolatedData = [];
    let data = [];
    let keys = Object.keys(rawData[0].data);
    let maxValue = max(rawData, (d) => max(Object.values(d.data)));

    // Ensure that we have enough space at the top for high dots/lines.
    // Offset by 1 to get the index / avoid extra Y-axis height above the
    // highest level.
    if (!isEmpty(this.lineData)) {
      let maxLineValue = max(this.lineData, (d) => d.value);
      if (maxLineValue > maxValue) {
        maxValue = maxLineValue - 1;
      }
    }
    if (!isEmpty(this.levels) && this.levels.length > maxValue) {
      maxValue = this.levels.length - 1;
    }

    // Preprocess to extrapolate lines to avoid weirdness at the bottom.
    extrapolatedData = rawData.reduce((extrapolated, tick, idx) => {
      // Deep-copy `data` (not just the shallow top-level clone) so this
      // extrapolation mutates our own working copy only. `this.data` is a
      // cached value owned by the caller (e.g. a controller's computed
      // property); mutating its nested `data` objects in place corrupted
      // that shared, cached array on every redraw.
      let processedTick = Object.assign({}, tick, {
        data: Object.assign({}, tick.data),
      });
      Object.keys(tick.data).forEach((key) => {
        let val = tick.data[key];
        if (val === 0) {
          let nextVal = 0;
          if (idx < rawData.length - 1) {
            nextVal = rawData[idx + 1].data[key];
          }

          if (nextVal > 0) {
            processedTick.data[key] = 0.5;
            if (idx > 0 && extrapolated[idx - 1].data[key] === 0) {
              // Clamp to 0: cumulative stack boundaries can't be negative.
              // Left unclamped, a large nextVal (e.g. a level index) pushes
              // this tick's boundary far below 0, sending the stacked area
              // path outside the chart's plottable [0, height] range.
              extrapolated[idx - 1].data[key] = Math.max(
                0,
                0.5 - (nextVal > 0.5 ? nextVal - 0.5 : nextVal),
              );
            }
          }
        }
      });
      extrapolated.push(processedTick);
      return extrapolated;
    }, []);

    // Convert extrapolated data into the right shape to be charted.
    extrapolatedData.forEach((tick) => {
      let prev = 0;
      let processed = {
        label: tick.label,
        value: tick.value,
      };

      Object.keys(tick.data).forEach((key) => {
        processed[key] = tick.data[key] - prev;
        prev = tick.data[key];
      });

      data.push(processed);
    });

    data.columns = keys;
    data.__maxValue = maxValue;

    return data;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    // Schedule a call to our `draw` method on Ember's "render" queue, which
    // will happen after the component has been placed in the DOM, and
    // subsequently each time data is changed.
    scheduleOnce('render', this, this.draw);
  }

  draw() {
    let plot = select(this.element);
    let data = this.processedData;
    if (isEmpty(data)) {
      // No point trying to chart an empty data set.
      return;
    }
    // SVG def ids (clip path, gradients) are document-global, so namespace them
    // per component instance. Otherwise, when more than one chart is on the page
    // (e.g. the report-view chart plus the review/edit chart) the ids collide
    // and `url(#clip)` resolves to another chart's clip rect, breaking clipping
    // and causing lines/areas to overflow the axes.
    let uid = this.elementId;
    let clipId = `clip-${uid}`;
    let gradientBottomId = `gradient-transparent-bottom-${uid}`;
    let gradientTopId = `gradient-transparent-top-${uid}`;
    let keys = data.columns;
    let margin = this.margin;
    let width = this.width - margin.left - margin.right;
    let height = this.height - margin.top - margin.bottom;

    let xStep = Math.floor(width / (data.length - 1));
    let xRange = data.map((_, idx) => idx * xStep);

    let x = scaleOrdinal().range(xRange),
      y = scaleLinear().range([height, 0]);

    let stack = d3Stack();

    let area = d3Area()
      .x(function (d) {
        return x(d.data.value);
      })
      .y0(function (d) {
        return y(d[0]);
      })
      .y1(function (d) {
        return y(d[1]);
      });

    let areaLine = d3Line()
      .x((d) => x(d.data.value))
      .y((d) => y(d[1]));

    // Remove previous chart (if there is one), then append the new root element.
    plot.selectAll('*').remove();

    // Create defs for clipping paths and gradients.
    let defs = plot.append('defs');

    // Add a clipping path that we can use to prevent the chart from spilling
    // over/behind the axes (i.e. due to extrapolation).
    defs
      .append('svg:clipPath')
      .attr('id', clipId)
      .append('svg:rect')
      .attr('id', `${clipId}-rect`)
      .attr('x', '0')
      .attr('y', '0')
      .attr('width', width)
      .attr('height', height);

    // Define vertical gradients for the boundary areas.
    let wellBelowColour = '#f93715';
    let wellAboveColour = '#525df4';

    if (this.colors.length > 4) {
      // Use the school's colours if available.
      wellBelowColour = this.colors[0];
      wellAboveColour = this.colors[4];
    }

    if (wellBelowColour && wellAboveColour) {
      // Bottom boundary gradient - diagonal from top-left to bottom-right
      let bottomBoundaryGradient = defs
        .append('linearGradient')
        .attr('id', gradientBottomId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%');

      // Start with "well below" colour at top left.
      bottomBoundaryGradient
        .append('stop')
        .attr('offset', '0%')
        .style('stop-color', wellBelowColour)
        .style('stop-opacity', 1.0);

      // Fade to 40% opacity at 40% of the distance.
      bottomBoundaryGradient
        .append('stop')
        .attr('offset', '40%')
        .style('stop-color', wellBelowColour)
        .style('stop-opacity', 0.4);

      // Fade to white at bottom right.
      bottomBoundaryGradient
        .append('stop')
        .attr('offset', '100%')
        .style('stop-color', '#ffffff')
        .style('stop-opacity', 1.0);

      // Top boundary gradient - diagonal from bottom-right to top-left.
      let topBoundaryGradient = defs
        .append('linearGradient')
        .attr('id', gradientTopId)
        .attr('x1', '100%')
        .attr('y1', '100%')
        .attr('x2', '0%')
        .attr('y2', '0%');

      // Start with "well above" colour at bottom right.
      topBoundaryGradient
        .append('stop')
        .attr('offset', '0%')
        .style('stop-color', wellAboveColour) // Well above colour at bottom-right
        .style('stop-opacity', 1.0); // Full opacity

      // Fade to 40% opacity at 40% of the distance.
      topBoundaryGradient
        .append('stop')
        .attr('offset', '40%') // Keep original color even longer (was 70%)
        .style('stop-color', wellAboveColour) // Same colour
        .style('stop-opacity', 0.4); // Still quite visible

      // Fade to fully transparent at top left.
      topBoundaryGradient
        .append('stop')
        .attr('offset', '100%')
        .style('stop-color', wellAboveColour)
        .style('stop-opacity', 0.0);
    }

    let g = plot
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    x.domain(data.map((d) => d.value));
    y.domain([0, data.__maxValue]);

    // Use the provided colours; fall back to schemeCategory10 if not provided.
    let colourArray = schemeCategory10;
    if (Array.isArray(this.colors)) {
      colourArray = this.colors;
    }

    if (keys.length === 5) {
      // Showing all colours.  Inject gradients for Well Below / Well Above.
      let belowIndex = 1;
      if (colourArray.length < 5) {
        // The provided colours don't include "Well Below".
        belowIndex = 0;
      }

      colourArray = [
        `url(#${gradientBottomId})`,
        colourArray[belowIndex], // Below
        colourArray[belowIndex + 1], // At
        colourArray[belowIndex + 2], // Above
        `url(#${gradientTopId})`,
      ];
    }

    let color = scaleOrdinal(colourArray);
    color.domain(keys);

    stack.keys(keys);

    let layer = g
      .selectAll('.layer')
      .data(stack(data))
      .enter()
      .append('g')
      .attr('class', 'layer');

    layer
      .append('path')
      .attr('class', 'area')
      .attr('fill-opacity', '0.3')
      .style('fill', (d) => color(d.key))
      .attr('d', area)
      .attr('clip-path', `url(#${clipId})`);

    let layerLine = g
      .selectAll('.layer-line')
      .data(stack(data).filter((d) => d.key !== 'Well above'))
      .enter()
      .append('g')
      .attr('class', 'layer-line')
      .append('g');

    layerLine
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', function (d) {
        return color(d.key);
      })
      .attr('stroke-width', 1)
      .attr('d', areaLine)
      .attr('clip-path', `url(#${clipId})`);

    if (this.showLabels) {
      layer
        .filter(function (d) {
          return d[d.length - 1][1] - d[d.length - 1][0] > 0.01;
        })
        .append('text')
        .attr('x', width - 6)
        .attr('y', function (d) {
          return y((d[d.length - 1][0] + d[d.length - 1][1]) / 2);
        })
        .attr('dy', '.35em')
        .style('font', '10px sans-serif')
        .style('text-anchor', 'end')
        .text(function (d) {
          return d.key;
        });
    }

    // Determine number of x-axis ticks to show labels for
    let numTicks = Math.floor(width / 72);
    numTicks = Math.min(numTicks, this.data.length);
    let tickDivisor = 1;
    if (numTicks < this.data.length) {
      let dividedTicks = Math.floor(this.data.length / tickDivisor);
      while (dividedTicks > numTicks) {
        tickDivisor++;
        dividedTicks = Math.floor(this.data.length / tickDivisor);
      }
    }

    // X-axis
    g.append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', 'translate(0,' + height + ')')
      .call(
        axisBottom(x)
          .ticks(5)
          .tickFormat((t) => {
            return this.tickLabel(t, tickDivisor);
          }),
      );

    // Y-axis with conditional level labels.
    if (this.showYAxisLabels) {
      // Determine maximum number of ticks with a minimum 25px between ticks.
      const maxTicks = Math.floor(height / 25);

      if (this.levels && this.levels.length > 0) {
        // Determine which levels to display ticks for, based on the maximum.
        const levelCount = this.levels.length;
        const tickStep = Math.max(1, Math.ceil(levelCount / maxTicks));

        const tickValues = [];
        for (let i = 0; i < levelCount; i += tickStep) {
          tickValues.push(i);
        }

        g.append('g')
          .attr('class', 'axis axis--y')
          .call(
            axisLeft(y)
              .tickValues(tickValues)
              .tickFormat((d) => {
                const levelIndex = Math.floor(d);
                const level = this.levels[levelIndex];
                return level ? level.get('briefLabel') : '';
              }),
          );
      } else {
        // Fall back to numeric labels if no levels were provided.
        const tickCount = Math.min(
          maxTicks,
          Math.max(1, Math.floor(data.__maxValue)),
        );

        g.append('g')
          .attr('class', 'axis axis--y')
          .call(axisLeft(y).ticks(tickCount));
      }
    } else {
      // No Y-axis labels - so no ticks.
      g.append('g').attr('class', 'axis axis--y').call(axisLeft(y).ticks(0));
    }

    // Render the student's progress line.
    let lineData = this.lineData;
    if (!isEmpty(lineData)) {
      let tooltip;
      if (!this.hideTooltip) {
        tooltip = tip()
          .attr('class', 'd3-tip')
          .html((d) => d.label);
        g.call(tooltip);
      }

      let line = d3Line()
        .x((d) => x(d.tick))
        .y((d) => y(d.value));

      g.append('path')
        .datum(lineData)
        .attr('fill', 'none')
        .attr('stroke', this.lineColor)
        .attr('stroke-linejoin', 'round')
        .attr('stroke-linecap', 'round')
        .attr('stroke-width', 1.5)
        .attr('d', line);

      let dots = g
        .selectAll('.dot')
        .data(lineData)
        .enter()
        .append('circle')
        .attr('fill', this.dotColor)
        // Transparent stroke to increase the tap target area for tooltip triggering on mobile
        .attr('stroke', 'transparent')
        .attr('stroke-width', 10)
        .attr('class', 'dot')
        .attr('cx', (d) => x(d.tick))
        .attr('cy', (d) => y(d.value))
        .attr('r', 3);

      if (!this.hideTooltip && tooltip) {
        dots.on('mouseover', tooltip.show).on('mouseout', tooltip.hide);
      }
    }

    // Fit the viewBox to what was actually drawn, rather than the fixed
    // "-10 0 width+10 height" allowance from ChartResizer. Axis-label overflow
    // past the nominal width/height depends on label text length and how many
    // ticks get skipped (both of which vary per chart and container width), so
    // a fixed allowance clips unpredictably for some milestone sets/screen
    // sizes while over-padding others. Measuring after render fits every case.
    //
    // getBBox() requires the SVG to actually have a rendering box: it throws
    // in Chrome/Safari, and returns a zeroed rect in Firefox, when the
    // element (or an ancestor) is undisplayed. That's exactly how printed
    // reports are built — report-printer.hbs renders the whole report,
    // charts included, into an off-screen `.report.hide` container and
    // copies its innerHTML into the print iframe — so an unguarded call here
    // either throws mid-draw or bakes a near-zero viewBox into the captured
    // markup, making the chart vanish on the printed output. Guard it and
    // keep whatever viewBox this chart already has (the caller-provided
    // default) when the bounds can't be measured.
    try {
      let bbox = plot.node().getBBox();
      if (bbox.width > 0 && bbox.height > 0) {
        let bboxPadding = 2;
        plot.attr(
          'viewBox',
          `${bbox.x - bboxPadding} ${bbox.y - bboxPadding} ${
            bbox.width + bboxPadding * 2
          } ${bbox.height + bboxPadding * 2}`,
        );
      }
    } catch {
      // Undisplayed SVG (e.g. mid-print capture) — leave the existing viewBox.
    }
  }

  tickLabel(val, divisor) {
    let data = this.data.find((point) => point.value === val);
    let index = this.data.indexOf(data);
    if (index % divisor === 0) {
      return data.label;
    }
    return '';
  }
}
